import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContact } from '../hooks/useContact';
import { ContactFormData } from '../services/contactService';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Field } from '../components/ui/Field';
// Field.hint used for validation messages
import { Input, Textarea, Select } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Valid email required'),
  phone: z.string().min(1, 'Phone is required'),
  messageType: z.string().min(1, 'Please select a topic'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

type FormValues = z.infer<typeof schema>;

const TOPICS = [
  { value: '', label: 'Select a topic…' },
  { value: 'shipping', label: 'Sales & pricing' },
  { value: 'account', label: 'Technical support' },
  { value: 'order', label: 'API & integrations' },
  { value: 'payment', label: 'Payment problem' },
  { value: 'returns', label: 'Returns & refunds' },
  { value: 'other', label: 'Other' },
];

const CONTACT_INFO = [
  { i: 'mail' as const, t: 'Email us', v: 'support@almaship.co', d: 'Sales · general inquiries' },
  { i: 'help' as const, t: 'Support', v: 'support@almaship.co', d: 'Account · shipments · billing' },
  { i: 'phone' as const, t: 'Call us', v: '+1 (302) 237-9963', d: 'Mon–Fri · 9am–6pm MST' },
  { i: 'building' as const, t: 'Visit', v: '1309 Coffeen Avenue STE 1200, Sheridan, Wyoming 82801', d: 'By appointment' },
];

const ContactPage: React.FC = () => {
  const { submitForm, isLoading, isSuccess, isError, error, result, reset } = useContact();

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', phone: '', messageType: '', message: '' },
  });

  const onSubmit = async (data: FormValues) => {
    await submitForm(data as ContactFormData);
  };

  React.useEffect(() => {
    if (isSuccess) resetForm();
  }, [isSuccess, resetForm]);

  return (
    <Page width="narrow">
      <PageHead>
        <div>
          <PageTitle>Get in touch</PageTitle>
          <PageSub>We usually reply within 2 hours during business hours.</PageSub>
        </div>
      </PageHead>

      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}
        className="details-grid"
      >
        {/* Form */}
        <Card style={{ padding: 28 }}>
          <h3 style={{ fontSize: 16, marginBottom: 14 }}>Send a message</h3>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Name" hint={errors.name?.message && <span style={{ color: 'var(--err)' }}>{errors.name.message}</span>}>
                <Input {...register('name')} placeholder="Your name" />
              </Field>
              <Field label="Email" hint={errors.email?.message && <span style={{ color: 'var(--err)' }}>{errors.email.message}</span>}>
                <Input {...register('email')} type="email" placeholder="you@example.com" />
              </Field>
            </div>

            <div style={{ marginTop: 12 }}>
              <Field label="Phone" hint={errors.phone?.message && <span style={{ color: 'var(--err)' }}>{errors.phone.message}</span>}>
                <Input {...register('phone')} placeholder="+1 (302) 237-9963" />
              </Field>
            </div>

            <div style={{ marginTop: 12 }}>
              <Field label="Topic" hint={errors.messageType?.message && <span style={{ color: 'var(--err)' }}>{errors.messageType.message}</span>}>
                <Select {...register('messageType')}>
                  {TOPICS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </Field>
            </div>

            <div style={{ marginTop: 12 }}>
              <Field label="Message" hint={errors.message?.message && <span style={{ color: 'var(--err)' }}>{errors.message.message}</span>}>
                <Textarea {...register('message')} rows={5} placeholder="How can we help?" />
              </Field>
            </div>

            <Button
              type="submit"
              variant="primary"
              style={{ width: '100%', marginTop: 14 }}
              disabled={isLoading}
            >
              <Icon name="send" size={14} /> {isLoading ? 'Sending…' : 'Send message'}
            </Button>
          </form>

          {result?.conversationUrl && (
            <div style={{ marginTop: 16 }}>
              <Callout tone="ok">
                Message received.{' '}
                <a href={result.conversationUrl} target="_blank" rel="noreferrer">
                  Open your private conversation
                </a>
              </Callout>
            </div>
          )}

          {isSuccess && !result?.conversationUrl && (
            <div style={{ marginTop: 16 }}>
              <Callout tone="ok">Your message has been sent! We'll get back to you soon.</Callout>
            </div>
          )}

          {isError && (
            <div style={{ marginTop: 16 }}>
              <Callout tone="warn">
                {error?.message || 'Something went wrong. Please try again.'}
              </Callout>
              <button
                onClick={reset}
                style={{ marginTop: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ink-3)' }}
              >
                Dismiss
              </button>
            </div>
          )}
        </Card>

        {/* Contact info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {CONTACT_INFO.map((c) => (
            <Card key={c.t} style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: 'var(--bg-tint)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon name={c.i} size={18} />
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 12 }}>{c.t}</div>
                  <div style={{ fontWeight: 600 }}>{c.v}</div>
                  <div className="muted" style={{ fontSize: 13 }}>{c.d}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Page>
  );
};

export default ContactPage;
